<template lang="pug">
    div.fluid.ma-0.pa-0.fill-height
        .pa-md-4.brown.lighten-5(style="height: 100vh;")
            .text-center(v-if="isLoading")
                v-progress-circular(
                    :size='100'
                    :width='10'
                    color='var(--volie-blue)'
                    indeterminate
                    style="margin-top: 100px;"
                )

            NoLoggedInQueues(
                v-else-if="renderNoLoggedInQueues"
            )

            NoCurrentCustomer(
                v-else-if="renderNoCurrentCustomer"
                text="We will send you a customer as soon as a new email becomes available"
                communication-type="Email"
                :organization-users="organizationUsers"
            )

            div(v-else)
                div(v-if="mainErrorAlert.show" style="margin-top: 2%;")
                    v-alert(type="error")
                        | {{ mainErrorAlert.text }}

                div(v-else)
                    v-row(dense style="height: 100vh;" ref="emailContentRow")
                        v-col(cols='12' md='8')
                            CustomerProfile.pa-2(
                                v-if="validCampaignCustomerActiveID"
                                v-bind:style="{ height: customerProfileHeight + 'px' }"
                                :appointment-is-unique-if-required="appointmentIsUniqueIfRequired"
                                :audio-email-incoming="audioEmailIncoming"
                                :audio-email-outcoming="audioEmailOutcoming"
                                communication-type="Email"
                                :communication-manager="communicationManager"
                                :complete-campaign-customer-active="completeCampaignCustomerActive"
                                :create-agent="createAgent"
                                :current-campaign-customer="currentCampaignCustomer"
                                :customer-profile-height="customerProfileHeight"
                                :handle-update-current-campaign-customer="handleUpdateCurrentCampaignCustomer"
                                :in-communication-center="true"
                                :is-manual-mode="isManualMode"
                                :is-rac-online="isRacOnline"
                                :organization-users="organizationUsers"
                                :set-appointment-is-unique-if-required="setAppointmentIsUniqueIfRequired"
                                :set-change-on-current-campaign-customer="setChangeOnCurrentCampaignCustomer"
                                :set-communication-manager="setCommunicationManager"
                                :set-is-okay-to-create-communication-disposition="setIsOkayToCreateCommunicationDisposition"
                                :set-selected-campaign-disposition="setSelectedCampaignDisposition"
                                :show-snackbar="showSnackbar"
                                source="communication_center"
                                :unassign-campaign-customer-active="unassignCampaignCustomerActive"
                            )
                            CustomerSearchCreate(
                                v-else-if="validCampaignID"
                                v-bind:style="{ height: customerProfileHeight + 'px' }"
                                communication-type="Email"
                                :current-campaign="currentCampaign"
                                :current-campaign-customer="currentCampaignCustomer"
                                :card-height="customerProfileHeight"
                                :create-agent="createAgent"
                                enrollment-purpose="return_email"
                                :initiate-create-communication-disposition="initiateCreateCommunicationDisposition"
                                :is-manual-mode="isManualMode"
                                :new-enrollment-success="newEnrollmentSuccess"
                                :set-is-okay-to-create-communication-disposition="setIsOkayToCreateCommunicationDisposition"
                                :set-selected-campaign-disposition="setSelectedCampaignDisposition"
                                :show-snackbar="showSnackbar"
                            )

                        v-col(cols='6' md='4')
                            EmailClient.pa-2(
                                v-bind:style="{ height: clientHeight + 'px' }"
                                :account-id="currentCampaignCustomer.account_id"
                                :audio-email-incoming="audioEmailIncoming"
                                :audio-email-outcoming="audioEmailOutcoming"
                                :client-height="clientHeight"
                                :create-agent="createAgent"
                                :current-campaign-customer="currentCampaignCustomer"
                                :is-rac-online="isRacOnline"
                                :show-snackbar="showSnackbar"
                            )

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState  from "@/store";

    // Components
    import CustomerProfile from '@/components/communication_center/shared/customer_profile.vue';
    import CustomerSearchCreate from '@/components/communication_center/shared/customer_search_create.vue';
    import EmailClient from '@/components/communication_center/email/email_client.vue';
    import NoCurrentCustomer from '@/components/communication_center/shared/no_current_customer.vue';
    import NoLoggedInQueues from '@/components/communication_center/shared/no_logged_in_queues.vue';

    // Data
    import {
        AssignReadyCampaignCustomers,
        UpdateCampaignCustomerActive,
    } from "@/data";

    // Helpers
    import { UpdateAssignedCustomerFromCustomerCommunicationManager } from "@/helpers/customer";
    import { applyIncomingTotalCommunicationDispositions } from "@/helpers/dispositions";

    export default Vue.extend({
        data() {
            return {
                appointmentIsUniqueIfRequired: true,
                bus: VolieState.websocketClient().eventBus(),
                campaignCustomers: [],
                clientHeight: null,
                communicationManager: null,
                contentRowHeight: null,
                currentCampaign: null,
                currentCampaignCustomer: null,
                customerProfileHeight: null,
                initiateCreateCommunicationDisposition: false,
                isLoading: false,
                isManualMode: false,
                isOkayToCreateCommunicationDisposition: false,
                isUpdatingCCActive: false,
                mainErrorAlert: {},
                manualModeCampaign: null,
                selectedCampaignDisposition: null,
            }
        },
        methods: {
            assignReadyCampaignCustomers(skipLoading) {

                // If the current user isn't logged into any queues, no need to run this request
                if (!this.hasLoggedInQueues) {
                    this.mainErrorAlert.show = true;
                    this.mainErrorAlert.text = "Please log into one or more communication queues to get started";
                    return;
                }

                const options = {
                    communication_type_name: "Email",
                    purpose: "return_email",
                }

                // Only load if necessary
                if (!skipLoading) {
                    this.isLoading = true;
                }

                AssignReadyCampaignCustomers(options).then((response) => {
                    if (!!response.Data && !!response.Data.campaign_customers && response.Data.campaign_customers.length > 0) {
                        this.campaignCustomers = response.Data.campaign_customers;
                        this.findOrSetInProgressCampaignCustomer();
                    } else {
                        this.isLoading = false;
                        this.campaignCustomers = [];
                        this.createAgentForAvailable();
                    }
                });

                return;
            },
            changeCampaignCustomers(otherCC) {

                // If the other record isn't the same as the current record, set record as current and update
                if (otherCC?.campaign_customer_active_id !== this.currentCampaignCustomer?.campaign_customer_active_id && otherCC.status === "assigned") {

                    // Set as current
                    this.currentCampaignCustomer = otherCC;

                    // Init options
                    const options = {
                        campaign_customer_active_id: this.currentCampaignCustomer.campaign_customer_active_id,
                        account_id: this.currentCampaignCustomer.account_id,
                        status: "in_progress",
                    }

                    // Update record in db
                    this.updateCampaignCustomerActive(options);
                }

                return;
            },
            completeCampaignCustomerActive() {

                // Init options
                const options = {
                    campaign_customer_active_id: this.currentCampaignCustomer?.campaign_customer_active_id,
                    account_id: this.currentCampaignCustomer?.account_id,
                    status: "completed",
                }

                // Update record in db
                this.updateCampaignCustomerActive(options);
            },
            createAgentForAvailable() {
                if (this.currentCampaignCustomer?.campaign_customer_active_id > 0) {
                    return;
                }

                const options = {
                    status_name: "Email - Available",
                }

                this.createAgent(options);
            },
            createAgentForCompletedCustomer() {
                if (!(this.currentCampaignCustomer?.campaign_customer_active_id > 0)) {
                    return;
                }

                const options = {
                    campaign_customer_id: this.currentCampaignCustomer.campaign_customer_id,
                    campaign_id: this.currentCampaignCustomer.campaign_id,
                    noticeable_id: this.currentCampaignCustomer.campaign_customer_id,
                    noticeable_type: "campaign_customer",
                    organization_id: this.currentCampaignCustomer.organization_id,
                    status_name: "Email - Completed Customer",
                    immediately_move_to_status: null,
                }

                // Move back to logged in this way so the records get created in order
                if (this.validForceCampaignCustomer) {
                    options.immediately_move_to_status = "Logged In";
                }

                this.createAgent(options);
            },
            createAgentForPause() {
                const options = {
                    status_name: "Pause",
                }

                this.createAgent(options);
            },
            createAgentForSavedForLater() {
                if (!(this.currentCampaignCustomer?.campaign_customer_active_id > 0)) {
                    return;
                }

                const options = {
                    campaign_customer_id: this.currentCampaignCustomer.campaign_customer_id,
                    campaign_id: this.currentCampaignCustomer.campaign_id,
                    noticeable_id: this.currentCampaignCustomer.campaign_customer_id,
                    noticeable_type: "campaign_customer",
                    organization_id: this.currentCampaignCustomer.organization_id,
                    status_name: "Email - Saved For Later",
                    immediately_move_to_status: null,
                }

                // Move back to logged in this way so the records get created in order
                if (this.validForceCampaignCustomer) {
                    options.immediately_move_to_status = "Logged In";
                }

                this.createAgent(options);
            },
            ensureOtherCampaignCustomersBackToAssigned() {
                if (!this.currentCampaignCustomer || !(this.campaignCustomers && this.campaignCustomers.length > 0)) {
                    return false;
                }

                for (let i = 0; i < this.campaignCustomers.length; i++) {

                    const other = this.campaignCustomers[i];
                    if (other?.campaign_customer_active_id !== this.currentCampaignCustomer.campaign_customer_active_id && other?.status === "in_progress") {
                        other.status = "assigned";
                    }
                }

                return true;
            },
            enterManualMode(campaign) {

                // Verify
                if (!(campaign?.campaign_id > 0)) {
                    return;
                }

                // Set manual mode fields
                this.currentCampaign = campaign;
                this.isManualMode = true;
                this.currentCampaignCustomer = null;
                this.saveAndNextAction = null;
                this.resetCurrentCampaignCustomerDefaults(false);

                // Create a cache of the manual mode campaign in case where user switches between inbound and manual mode
                // Needs to be a separate reference because currentCampaign gets reset to null via inbound
                this.manualModeCampaign = { ...campaign };

                // Run in next tick so screen resets properly
                Vue.nextTick(() => {

                    // Reinit current cc with temp object
                    this.currentCampaignCustomer = {
                        campaign_customer_active_id: null,
                        account_id: this.currentCampaign.account_id,
                        assigned_to: null,
                        campaign_customer_id: null,
                        campaign_id: this.currentCampaign.campaign_id,
                        current_scheduled_communication_id: null,
                        customer_id: null,
                        customer_vehicle_id: null,
                        forte_crm_key_id: null,
                        gm_ro_bac_is_valid: null,
                        inline_crm_key: null,
                        next_attemptable_id: null,
                        next_attemptable_type: null,
                        noticeable_id: null,
                        noticeable_type: null,
                        organization_id: this.currentCampaign.organization_id,
                        organization_time_zone_id: this.currentCampaign.organization_time_zone_id,
                        account_forte_crm_enabled: false,
                        account_inline_crm_enabled: false,
                        campaign_name: this.currentCampaign.campaign_name,
                        enrolled_date: null,
                        expiration_code: null,
                        next_attemptable_value: null,
                        next_attempt_number: null,
                        organization_name: this.currentCampaign.organization_name,
                        prospect_id: null,
                        purpose: "return_email",
                        status: null,
                        total_communication_dispositions: 0,
                        mykaarma_enabled: false,
                        tekion_enabled: false,
                        toyoguard_enabled: false,
                        user_email: null,
                        user_first_name: null,
                        user_last_name: null,
                        conversation_group: null,
                        customer: null,
                        incoming_phone_number: { ...this.currentCampaign.incoming_phone_number },
                        vin_enabled: false,
                        xtime_enabled: false,
                    }

                    // Move agent into manual email status
                    this.createAgentForStartManualEmail();

                    // Stop loader
                    this.isLoading = false;

                    // Resize window
                    Vue.nextTick(() => {
                        this.handleWindowResize();
                    });
                });
            },
            exitManualMode() {

                // Unassign if someone is in scope
                if (this.currentCampaignCustomer?.campaign_customer_active_id > 0) {
                    this.unassignCampaignCustomerActive(true);
                }

                this.currentCampaign = null;
                this.manualModeCampaign = null;
                this.isManualMode = false;
                this.currentCampaignCustomer = null;
                this.resetCurrentCampaignCustomerDefaults(false);

                // Reinit screen
                this.getNext();
            },
            executeRemainingCreatedActions() {
                // If force is present, set and init screen
                if (this.validForceCampaignCustomer) {

                    // Set as current
                    this.currentCampaignCustomer = { ...this.forceCampaignCustomer };

                } else {

                    // Load normal screen
                    this.getNext();
                }
            },
            findOrSetInProgressCampaignCustomer() {
                if (!(!!this.campaignCustomers && this.campaignCustomers.length > 0)) {
                    return;
                }

                // Loop through campaign customers
                for (let i = 0; i < this.campaignCustomers.length; i++) {

                    // Init campaign customer
                    const campaignCustomer = this.campaignCustomers[i];

                    // If the status is present, set as current
                    if (!this.currentCampaignCustomer && campaignCustomer?.account_id > 0 && campaignCustomer?.campaign_customer_active_id > 0 && campaignCustomer?.status === 'in_progress') {

                        // Set
                        this.currentCampaignCustomer = campaignCustomer;

                        break;
                    }
                }

                // If zero in progress records in array, take the first record and mark it as in progress
                if (!this.currentCampaignCustomer) {

                    // Set campaign customer on data
                    this.currentCampaignCustomer = this.campaignCustomers[0];

                    // Update campaign customer to 'in progress'
                    this.updateCampaignCustomerActive({ account_id: this.currentCampaignCustomer?.account_id, campaign_customer_active_id: this.currentCampaignCustomer?.campaign_customer_active_id, status: "in_progress" });
                } else {

                    // Set isLoading back to false since we already have our current campaign customer
                    this.isLoading = false;
                }

                return;
            },
            getNext() {
                return this.isManualMode ? this.enterManualMode(this.manualModeCampaign) : this.assignReadyCampaignCustomers(false);
            },
            handleAfterCampaignCustomerActive(ccActiveID, skipAgentCreateInForce, skipAssignLoading) {

                // If present, handle
                if (ccActiveID > 0) {

                    // This will not be present when doing a force campaign customer via the manager live dashboard
                    if (this.campaignCustomers?.length > 0) {

                        // Find idx
                        const idx = this.campaignCustomers.findIndex(i => i.campaign_customer_active_id === ccActiveID);
                        if (idx >= 0) {

                            // Remove from slice
                            this.campaignCustomers.splice(idx, 1);
                        }
                    }

                    // Determine if we need to reinit
                    if (this.currentCampaignCustomer?.campaign_customer_active_id > 0 && this.currentCampaignCustomer?.campaign_customer_active_id === ccActiveID) {
                        this.currentCampaignCustomer = null;
                    }
                }

                // If we were here for a single record, return. Otherwise assign next
                return this.validForceCampaignCustomer ? this.exitForceCampaignCustomer(skipAgentCreateInForce) : this.assignReadyCampaignCustomers(skipAssignLoading);
            },
            handleUpdateCurrentCampaignCustomer(options) {

                // Verify same customer
                if (!(options?.customer_id > 0 && options.customer_id === this.currentCampaignCustomer?.customer?.customer_id)) {
                    return;
                }

                // Set customer params
                this.currentCampaignCustomer.customer = UpdateAssignedCustomerFromCustomerCommunicationManager(options, this.currentCampaignCustomer.customer);

                return;
            },
            handleWindowResize() {

                // Declare constants
                const paddingPx = 32;
                const topHeader = 64;

                // Calcuate
                this.contentRowHeight = this.$refs.emailContentRow?.clientHeight - topHeader;
                this.customerProfileHeight = this.contentRowHeight - paddingPx;
                this.clientHeight = this.customerProfileHeight;
            },
            hasCampaignCustomerActive(ccActiveID) {

                // Verify
                if (!ccActiveID) {
                    return false;
                }

                // Verify that the current user either has it as the "current campaign customer" or has it in it's assigned slice
                if (this.currentCampaignCustomer?.campaign_customer_active_id > 0 && this.currentCampaignCustomer?.campaign_customer_active_id === ccActiveID) {
                    return true;
                }

                // This will not be present when doing a force campaign customer via the manager live dashboard
                if (this.campaignCustomers?.length > 0) {

                    // Find idx
                    const idx = this.campaignCustomers.findIndex(i => i.campaign_customer_active_id === ccActiveID);
                    if (idx >= 0) {
                        return true;
                    }
                }

                return false;
            },
            initMainErrorAlert() {
                this.mainErrorAlert = {
                    show: false,
                    text: null,
                }
            },
            moveCampaignCustomerActiveToInProgress() {

                // Verify legit cc active and that we aren't already in progress status
                if (!(this.currentCampaignCustomer?.campaign_customer_active_id > 0) || this.currentCampaignCustomer.status === "in_progress") {
                    return;
                }

                // Init options
                const options = {
                    campaign_customer_active_id: this.currentCampaignCustomer.campaign_customer_active_id,
                    account_id: this.currentCampaignCustomer.account_id,
                    status: "in_progress",
                }

                // Update record in db
                this.updateCampaignCustomerActive(options, true);
            },
            newEnrollmentSuccess(ccActive, skipStatusMove) {

                // Verify
                if (!(ccActive?.campaign_customer_active_id > 0)) {
                    return;
                }

                // If null, reset back to empty array
                if (!this.campaignCustomers) {
                    this.campaignCustomers = [];
                }

                // If null, set to empty object
                if (!this.currentCampaignCustomer) {
                    this.currentCampaignCustomer = {};
                }

                // // Set fields on cc active pointer
                this.currentCampaignCustomer.campaign_customer_active_id = ccActive.campaign_customer_active_id;
                this.currentCampaignCustomer.account_id = ccActive.account_id;
                this.currentCampaignCustomer.assigned_to = ccActive.assigned_to;
                this.currentCampaignCustomer.campaign_customer_id = ccActive.campaign_customer_id;
                this.currentCampaignCustomer.campaign_id = ccActive.campaign_id;
                this.currentCampaignCustomer.current_scheduled_communication_id = ccActive.current_scheduled_communication_id;
                this.currentCampaignCustomer.customer_id = ccActive.customer_id;
                this.currentCampaignCustomer.customer_vehicle_id = ccActive.customer_vehicle_id;
                this.currentCampaignCustomer.forte_crm_key_id = ccActive.forte_crm_key_id;
                this.currentCampaignCustomer.gm_ro_bac_is_valid = ccActive.gm_ro_bac_is_valid;
                this.currentCampaignCustomer.inline_crm_key = ccActive.inline_crm_key;
                this.currentCampaignCustomer.next_attemptable_id = ccActive.next_attemptable_id;
                this.currentCampaignCustomer.next_attemptable_type = ccActive.next_attemptable_type;
                this.currentCampaignCustomer.noticeable_id = ccActive.noticeable_id;
                this.currentCampaignCustomer.noticeable_type = ccActive.noticeable_type;
                this.currentCampaignCustomer.organization_id = ccActive.organization_id;
                this.currentCampaignCustomer.organization_time_zone_id = ccActive.organization_time_zone_id;
                this.currentCampaignCustomer.account_forte_crm_enabled = ccActive.account_forte_crm_enabled;
                this.currentCampaignCustomer.account_inline_crm_enabled = ccActive.account_inline_crm_enabled;
                this.currentCampaignCustomer.mykaarma_enabled = this.resolveMykaarmaScreenPopEnabled(
                    ccActive.organization_id,
                    ccActive.mykaarma_enabled,
                );
                this.currentCampaignCustomer.tekion_enabled = ccActive.tekion_enabled;
                this.currentCampaignCustomer.toyoguard_enabled = ccActive.toyoguard_enabled;
                this.currentCampaignCustomer.campaign_name = ccActive.campaign_name;
                this.currentCampaignCustomer.enrolled_date = ccActive.enrolled_date;
                this.currentCampaignCustomer.expiration_code = ccActive.expiration_code;
                this.currentCampaignCustomer.next_attemptable_value = ccActive.next_attemptable_value;
                this.currentCampaignCustomer.next_attempt_number = ccActive.next_attempt_number;
                this.currentCampaignCustomer.organization_name = ccActive.organization_name;
                this.currentCampaignCustomer.prospect_id = ccActive.prospect_id;
                this.currentCampaignCustomer.purpose = ccActive.purpose;
                this.currentCampaignCustomer.status = ccActive.status;
                this.currentCampaignCustomer.total_communication_dispositions = ccActive.total_communication_dispositions;
                this.currentCampaignCustomer.user_email = ccActive.user_email;
                this.currentCampaignCustomer.user_first_name = ccActive.user_first_name;
                this.currentCampaignCustomer.user_last_name = ccActive.user_last_name;
                this.currentCampaignCustomer.conversation_group = ccActive.conversation_group;
                this.currentCampaignCustomer.customer = ccActive.customer;
                this.currentCampaignCustomer.vin_enabled = ccActive.vin_enabled;
                this.currentCampaignCustomer.is_integrations_api_calendar_ui_enabled = ccActive.is_integrations_api_calendar_ui_enabled;
                this.currentCampaignCustomer.xtime_enabled = ccActive.xtime_enabled;

                // Run only if we need to
                if (!skipStatusMove) {

                    // Move cc active into in progress
                    this.moveCampaignCustomerActiveToInProgress();
                }

                // Resize window
                Vue.nextTick(() => {
                    this.handleWindowResize();
                });
            },
            onCampaignCustomerActiveStatusChange(response) {

                // Verify legit cc
                const campaignCustomer = response?.Data;
                if (!(campaignCustomer?.campaign_customer_active_id > 0) || !(campaignCustomer?.account_id > 0) || !(campaignCustomer?.purpose?.length > 0) || !(campaignCustomer?.status?.length > 0)) {
                    return;
                }

                // Verify purpose is return message
                if (campaignCustomer.purpose !== "return_email") {
                    return;
                }

                // Only need to do something when status is "queued" or "completed" (12/1/2020)
                if (campaignCustomer.status !== "queued" && campaignCustomer.status !== "completed") {
                    return;
                }

                // If the current user is the one doing the updating, let the function response handlers control the flow
                if (this.isUpdatingCCActive) {
                    return;
                }

                // Verify current user has this cc active - otherwise no need to run handle
                // We don't want to run the handle for user's who don't need to
                if (!this.hasCampaignCustomerActive(campaignCustomer.campaign_customer_active_id)) {
                    return;
                }

                // Init
                const skipAgentCreateInForce = false;
                let skipAssignLoading = false;

                // Need to skip assign loading if the current user is the agent in the communication center and the record is not up on the agent's screen
                // If valid force is false, then the current user has to be in the communication center
                if (!this.validForceCampaignCustomer && this.currentCampaignCustomer?.campaign_customer_active_id > 0 && this.currentCampaignCustomer?.campaign_customer_active_id !== campaignCustomer.campaign_customer_active_id) {
                    skipAssignLoading = true;
                }

                this.handleAfterCampaignCustomerActive(campaignCustomer.campaign_customer_active_id, skipAgentCreateInForce, skipAssignLoading);
            },
            onNewAssignedCampaignCustomer(response) {

                // Verify legit cc
                const campaignCustomer = response?.Data;
                if (!(campaignCustomer?.campaign_customer_active_id > 0) || !(campaignCustomer?.account_id > 0) || !(campaignCustomer?.assigned_to > 0)) {
                    return;
                }

                // Verify assigned to is current user
                if (this.currentUser?.user_id !== campaignCustomer.assigned_to) {
                    return;
                }

                // Reinit if null
                if (this.campaignCustomers === null || this.campaignCustomers === undefined) {
                    this.campaignCustomers = [];
                }

                // Determine if record already is in array
                const idx = this.campaignCustomers.findIndex(i => i.campaign_customer_active_id === campaignCustomer.campaign_customer_active_id);
                if (idx >= 0) {
                    return;
                }

                // Push onto array
                this.campaignCustomers.push(campaignCustomer);

                // If this is the only campaign customer assigned to the user, promote to in progress
                if (this.campaignCustomers?.length === 1) {
                    this.changeCampaignCustomers(campaignCustomer);
                }
            },
            onNewCommunicationDisposition(response) {

                // Verify legit communication disposition
                const data = response?.Data;
                const communicationDisposition = data?.communication_disposition;
                if (!(communicationDisposition?.communication_disposition_id > 0) || !(communicationDisposition?.campaign_customer_active_id > 0) || !(communicationDisposition?.account_id > 0)) {
                    return;
                }

                // If force, then the current user is a manager and the current cc should match
                // Otherwise, need to check array of campaign customers
                if (this.validForceCampaignCustomer) {

                    // Verify the campaign customer is the same
                    if (this.currentCampaignCustomer?.campaign_customer_active_id !== communicationDisposition.campaign_customer_active_id) {
                        return;
                    }

                    applyIncomingTotalCommunicationDispositions(this.currentCampaignCustomer, data?.total_communication_dispositions);
                } else {

                    // Verify array has records
                    if (!(this.campaignCustomers?.length > 0)) {
                        return;
                    }

                    // Loop through
                    for (let i = 0; i < this.campaignCustomers.length; i++) {

                        // Init
                        const ccActive = this.campaignCustomers[i];

                        // Verify the campaign customer is the same and if so, set total
                        if (ccActive?.campaign_customer_active_id === communicationDisposition.campaign_customer_active_id) {
                            applyIncomingTotalCommunicationDispositions(ccActive, data?.total_communication_dispositions);
                            break;
                        }
                    }
                }
            },
            onRequeuedIdleCampaignCustomerActive(response) {

                // Verify legit cc
                const campaignCustomer = response?.Data;
                if (!(campaignCustomer?.campaign_customer_active_id > 0) || !(campaignCustomer?.account_id > 0)) {
                    return;
                }

                // Verify user has campaign customers
                if (!(this.campaignCustomers?.length > 0)) {
                    return;
                }

                // Determine if record is in array
                const idx = this.campaignCustomers.findIndex(i => i.campaign_customer_active_id === campaignCustomer.campaign_customer_active_id);
                if (idx < 0) {
                    return;
                }

                // Remove from array
                this.campaignCustomers.splice(idx, 1);

                // If the record is the assigned record, unassign
                if (this.currentCampaignCustomer?.campaign_customer_active_id === campaignCustomer.campaign_customer_active_id) {
                    this.currentCampaignCustomer = null;
                }

                // Move user to Pause
                this.createAgentForPause();
            },
            resetCurrentCampaignCustomerDefaults(enrollmentSkip) {
                this.communicationManager = null;
                this.initiateCreateCommunicationDisposition = false;
                this.isUpdatingCCActive = false;
                this.mainErrorAlert = {};

                // Enrollment skip
                if (!enrollmentSkip) {
                    this.isOkayToCreateCommunicationDisposition = false;
                    this.selectedCampaignDisposition = null;
                }
            },
            resyncScreenForInternetLoss() {

                // Reload assign ready campaign customers
                this.assignReadyCampaignCustomers(true);
            },
            setAppointmentIsUniqueIfRequired(value) {
                this.appointmentIsUniqueIfRequired = value;
            },
            setChangeOnCurrentCampaignCustomer(key, value) {
                switch(key) {
                case "total_communication_dispositions":
                    applyIncomingTotalCommunicationDispositions(this.currentCampaignCustomer, value);
                }
            },
            setCommunicationManager(communicationManager) {
                this.communicationManager = communicationManager;
            },
            setIsOkayToCreateCommunicationDisposition(isOkayToCreateCommunicationDisposition) {
                this.isOkayToCreateCommunicationDisposition = isOkayToCreateCommunicationDisposition === true ? true : false;
            },
            setSelectedCampaignDisposition(campaignDisposition) {
                this.selectedCampaignDisposition = campaignDisposition;
            },
            createAgentForStartManualEmail() {
                if (!(this.currentCampaignCustomer?.campaign_id > 0)) {
                    return;
                }

                const options = {
                    campaign_customer_id: this.currentCampaignCustomer.campaign_customer_id,
                    campaign_id: this.currentCampaignCustomer.campaign_id,
                    noticeable_id: this.currentCampaignCustomer.campaign_customer_id,
                    noticeable_type: "campaign_customer",
                    organization_id: this.currentCampaignCustomer.organization_id,
                    status_name: "Start Manual Email",
                }

                this.createAgent(options);
            },
            unassignCampaignCustomerActive() {

                // Init options
                const options = {
                    campaign_customer_active_id: this.currentCampaignCustomer?.campaign_customer_active_id,
                    account_id: this.currentCampaignCustomer?.account_id,
                    status: "queued",
                }

                // Update record in db
                this.updateCampaignCustomerActive(options);
            },
            updateCampaignCustomerActive(options) {
                this.isLoading = true;
                this.isUpdatingCCActive = true;
                UpdateCampaignCustomerActive(options).then((response) => {

                    // If the response's campaign customer active matches the current record, set changes
                    if (response?.Data?.campaign_customer_active?.id > 0 && response?.Data?.campaign_customer_active?.id === this.currentCampaignCustomer?.campaign_customer_active_id) {
                        this.currentCampaignCustomer.status = response.Data.campaign_customer_active.status;

                        // Switch over status
                        switch(this.currentCampaignCustomer.status) {

                        // If record is now completed, null current and rerun assign ready campaign customers
                        case "completed":
                            this.createAgentForCompletedCustomer();
                            this.handleAfterCampaignCustomerActive(this.currentCampaignCustomer.campaign_customer_active_id, true, false);
                            break;

                        // If this record is now in progress, loop through other records and set back to assigned
                        case "in_progress":
                            // this.createAgentForOpenedInbox();
                            this.ensureOtherCampaignCustomersBackToAssigned();
                            break;

                        // If the record is now queued, record was "saved for later", null current and rerun assign ready cc
                        case "queued":
                            this.createAgentForSavedForLater();
                            this.handleAfterCampaignCustomerActive(this.currentCampaignCustomer.campaign_customer_active_id, true, false);
                            break;

                        }
                    }

                    this.isLoading = false;
                    this.isUpdatingCCActive = false;
                });
            },
        },
        computed: {
            currentUser() {
                return VolieState.currentUser();
            },
            renderNoCurrentCustomer() {

                // If loading, do not render
                if (this.isLoading) {
                    return false;
                }

                // If manual mode but missing campaign ID, render. Otherwise render if missing cc active ID
                return this.isManualMode ? !this.validCampaignID : (!this.validCampaignCustomerActiveID && !this.isLoading);
            },
            renderNoLoggedInQueues() {
                return !this.hasLoggedInQueues && !this.validForceCampaignCustomer;
            },
            userDomInteraction() {
                return VolieState.userDomInteraction();
            },
            validCampaignCustomerActiveID() {
                return this.currentCampaignCustomer?.campaign_customer_active_id > 0;
            },
            validCampaignID() {
                return this.currentCampaignCustomer?.campaign_id > 0;
            },
            validForceCampaignCustomer() {
                return this.forceCampaignCustomer?.campaign_customer_active_id > 0;
            },
        },
        components: {
            CustomerProfile,
            CustomerSearchCreate,
            EmailClient,
            NoCurrentCustomer,
            NoLoggedInQueues,
        },
        props: {
            audioEmailIncoming: HTMLAudioElement,
            audioEmailOutcoming: HTMLAudioElement,
            createAgent: Function,
            exitForceCampaignCustomer: Function,
            forceCampaign: Object,
            forceCampaignCustomer: Object,
            getCommunicationCenter: Function,
            hasLoggedInQueues: Boolean,
            isRacOnline: Boolean,
            isVinEnabled: Boolean,
            organizationUsers: Array,
            resolveMykaarmaScreenPopEnabled: {
                type: Function,
                default: (_organizationId, serverEnabled) => serverEnabled === true,
            },
            showSnackbar: Function,
        },
        destroyed() {
            window.removeEventListener('resize', this.handleWindowResize);
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "campaignCustomerActiveStatusChange" }}, this.onCampaignCustomerActiveStatusChange);
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "newAssignedCampaignCustomer" }}, this.onNewAssignedCampaignCustomer);
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "newCommunicationDisposition" }}, this.onNewCommunicationDisposition);
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "requeuedIdleCampaignCustomerActive" }}, this.onRequeuedIdleCampaignCustomerActive);
            return;
        },
        created() {
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "campaignCustomerActiveStatusChange" }}, this.onCampaignCustomerActiveStatusChange);
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "newAssignedCampaignCustomer" }}, this.onNewAssignedCampaignCustomer);
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "newCommunicationDisposition" }}, this.onNewCommunicationDisposition);
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "requeuedIdleCampaignCustomerActive" }}, this.onRequeuedIdleCampaignCustomerActive);
            this.initMainErrorAlert();

            // if has logged in queues is false, double check prop is not stale
            
            this.executeRemainingCreatedActions();

            return;
        },
        mounted() {
            window.addEventListener('resize', this.handleWindowResize);
            this.handleWindowResize();
        },
        watch: {
            forceCampaign: {
                deep: true,
                immediate: true,
                handler() {
                    if (this.forceCampaign?.campaign_id > 0) {
                        this.enterManualMode({ ...this.forceCampaign });
                    }
                },
            },
            hasLoggedInQueues: {
                deep: true,
                immediate: true,
                handler(newValue, oldValue) {
                    if (oldValue !== newValue && newValue === true && oldValue === false && !this.validForceCampaignCustomer) {
                        this.initMainErrorAlert();
                        this.executeRemainingCreatedActions();
                    }
                },
            },
            isLoading() {
                Vue.nextTick(() => { this.handleWindowResize(); });
            },
            isRacOnline() {

                // If it changed and now is true again, need to re-sync their assigned customers and cc deployments in case they missed some realtime requests
                if (this.isRacOnline === true) {
                    this.resyncScreenForInternetLoss()
                }
            },
        },
    });
</script>

<style lang="scss" scoped>
</style>
